<script>
import { mapState, mapActions, mapGetters, mapMutations } from "vuex";
import FooterWrapper from "@/components/FooterWrapper";

export default {
  // Included components
  components: {
    FooterWrapper,
  },

  data() {
    return {
      // The ID of the invitation
      invitationId: null,

      // The actual invitation object
      invitation: null,
    };
  },

  computed: {
    // Whether the user is already in the team they're being invited to
    alreadyInTeam() {
      return Object.keys(this.teams).indexOf(this.invitation.team.id) !== -1;
    },

    // Whether the user is accepting the invitation from a different
    // email than where the invitation was sent
    wrongEmail() {
      return this.user.email != this.invitation.email;
    },

    // Mapped vuex state
    ...mapState(["user", "teams"]),
    ...mapGetters(["authenticated"]),
  },

  // When created, fetch the invitation. If the user is not logged in,
  // redirect them to the log in page. If the invitation does not exist
  // send them to the home page.
  async created() {
    this.invitationId = this.$route.params.invitationId;

    if (!this.authenticated) {
      return;
    }

    let invitation = await this.getInvitation({
      invitationId: this.invitationId,
    });
    if (!invitation) {
      this.$router.push("/");
    }
    this.invitation = invitation;
  },

  methods: {
    // Attempt to accept the invitation
    async submit() {
      let success = await this.acceptInvitation(this.invitationId);
      if (success) {
        await this.getTeams();
        this.setActiveTeamId(this.invitation.team.id);
        this.$router.push(`/team`);
      }
    },

    async reject() {
      let success = await this.rejectInvitation(this.invitationId);
      if (success) {
        // reload current invitation
        this.invitationId = this.$route.params.invitationId;
        if (!this.authenticated) {
          return;
        }

        let invitation = await this.getInvitation({
          invitationId: this.invitationId,
        });

        this.invitation = invitation;
      }
    },

    // Mapped vuex actions
    ...mapActions([
      "getInvitation",
      "acceptInvitation",
      "rejectInvitation",
      "getTeams",
    ]),
    ...mapMutations(["pushToast", "setActiveTeamId"]),
  },
};
</script>

<template>
  <FooterWrapper>
    <b-container class="mt-3" v-if="!authenticated">
      <h1>Accept Invitation</h1>
      <b-card class="text-center py-5">
        <b-card-title>You must sign in to view this invitation</b-card-title>

        <b-card-text>
          <b-button
            class="ml-3"
            variant="primary"
            :to="{
              path: '/sign-in',
              query: { next: `/invitations/${this.invitationId}` },
            }"
          >
            Log In
          </b-button>
          <!-- or
          <b-link
            :to="{
              path: '/sign-up',
              query: { invitationId: this.invitationId }
            }"
          >
            Sign Up
          </b-link>
          -->
        </b-card-text>
      </b-card>
    </b-container>
    <b-container class="mt-3" v-else>
      <h1>Review Invitation</h1>
      <b-card class="text-center py-5">
        <div v-if="invitation">
          <b-card-title>{{ invitation.team.name }}</b-card-title>
          <b-card-text>
            {{
              invitation.created_by.name || "@" + invitation.created_by.handle
            }}
            invited you to join the
            <b>#{{ invitation.team.handle }}</b>
            team.
          </b-card-text>
          <b-card-text v-if="alreadyInTeam">
            You are already a member of this team. Sign in using a different
            account to accept this invitation.
          </b-card-text>
          <b-card-text v-else-if="invitation.accepted">
            This invitation has already been accepted.
          </b-card-text>
          <b-card-text v-else-if="invitation.rejected">
            This invitation has been rejected. If this a mistake and you need a
            new invitation, ask a member of the team to send you a new one.
          </b-card-text>
          <div v-else>
            <b-card-text v-if="wrongEmail">
              Note that the invitation was sent to
              <b>{{ invitation.email }}</b>
              , but you're signed in as
              <b>{{ user.email }}</b>
              .
            </b-card-text>
            <b-button variant="success" @click="submit">
              Accept Invitation
            </b-button>
            <b-button class="ml-3" variant="danger" @click="reject">
              Decline Invitation
            </b-button>
          </div>
        </div>
      </b-card>
    </b-container>
  </FooterWrapper>
</template>
